<script>
  import Layout from '../components/Layout.svelte';
  import { onMount } from 'svelte';
  import { navigate } from 'svelte-routing';

  let jobs = [];
  let loading = true;
  let error = null;
  let showClosed = false;
  let searchQuery = '';

  $: filteredJobs = getFilteredJobs(jobs, showClosed, searchQuery);

  function getFilteredJobs(allJobs, includeClosed, search) {
    let filtered = [...allJobs];
    if (!includeClosed) {
      filtered = filtered.filter(j => !j.closedOn);
    }
    if (search && search.trim().length >= 2) {
      const q = search.toLowerCase();
      filtered = filtered.filter(j =>
        (j.name && j.name.toLowerCase().includes(q)) ||
        (j.locationName && j.locationName.toLowerCase().includes(q)) ||
        (j.status && j.status.toLowerCase().includes(q))
      );
    }
    return filtered;
  }

  async function fetchJobs() {
    try {
      loading = true;
      error = null;
      const params = new URLSearchParams();
      params.set('includeClosed', 'true');
      const response = await fetch(`/api/jt/cost-reporting/jobs?${params.toString()}`);
      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401 && data.needsLogin) {
          error = 'Please configure your JobTread credentials in Settings';
          return;
        }
        throw new Error(data.error || 'Failed to fetch jobs');
      }

      jobs = data.jobs || [];
    } catch (err) {
      error = err.message;
      console.error('Error fetching jobs:', err);
    } finally {
      loading = false;
    }
  }

  function viewReport(jobId) {
    navigate(`/cost-reporting/${jobId}`);
  }

  onMount(() => {
    fetchJobs();
  });
</script>

<Layout activeRoute="/cost-reporting">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900">Cost Reporting</h1>
      <p class="mt-1 text-sm text-gray-500">Select a job to view its detailed cost report.</p>
    </div>

    <!-- Filters -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
      <div class="flex flex-col sm:flex-row sm:items-center gap-4">
        <div class="flex-1">
          <input
            type="text"
            bind:value={searchQuery}
            placeholder="Search by job name, location, or status..."
            class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <label class="flex items-center gap-2 text-sm text-gray-700 cursor-pointer whitespace-nowrap">
          <input
            type="checkbox"
            bind:checked={showClosed}
            class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          Show Closed Jobs
        </label>
      </div>
    </div>

    {#if loading}
      <div class="flex items-center justify-center py-12">
        <svg class="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span class="ml-3 text-gray-600">Loading jobs...</span>
      </div>
    {:else if error}
      <div class="bg-red-50 border border-red-200 rounded-lg p-4">
        <p class="text-red-800 text-sm">{error}</p>
        <button
          on:click={fetchJobs}
          class="mt-2 text-sm text-red-700 underline hover:text-red-900"
        >
          Try again
        </button>
      </div>
    {:else if filteredJobs.length === 0}
      <div class="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
        <p class="text-gray-600">No jobs found{searchQuery ? ' matching your search' : ''}.</p>
      </div>
    {:else}
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <!-- Table Header -->
        <div class="hidden sm:grid sm:grid-cols-[1fr_1fr_0.6fr_auto] gap-4 px-6 py-3 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-400 uppercase tracking-wider">
          <div>Job Name</div>
          <div>Location</div>
          <div>Status</div>
          <div class="text-right">Action</div>
        </div>

        <!-- Table Rows -->
        {#each filteredJobs as job (job.id)}
          <div class="grid grid-cols-1 sm:grid-cols-[1fr_1fr_0.6fr_auto] gap-2 sm:gap-4 px-6 py-5 border-b border-gray-100 hover:bg-[#f0f4ff] transition-colors items-center"
               style="min-height: 4rem;">
            <div>
              <span class="sm:hidden text-xs font-semibold text-gray-400 uppercase">Job Name</span>
              <p class="text-sm font-medium text-gray-900">{job.name}</p>
            </div>
            <div>
              <span class="sm:hidden text-xs font-semibold text-gray-400 uppercase">Location</span>
              <p class="text-sm text-gray-700">{job.locationName || '-'}</p>
            </div>
            <div>
              <span class="sm:hidden text-xs font-semibold text-gray-400 uppercase">Status</span>
              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                {job.closedOn ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-800'}">
                {job.status || 'Open'}
              </span>
            </div>
            <div class="sm:text-right">
              <button
                on:click={() => viewReport(job.id)}
                class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#2a3550] hover:bg-[#3d5880] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                Detailed Cost Report
              </button>
            </div>
          </div>
        {/each}
      </div>

      <div class="mt-4 text-sm text-gray-500">
        Showing {filteredJobs.length} job{filteredJobs.length !== 1 ? 's' : ''}
      </div>
    {/if}
  </div>
</Layout>
